/**
 * AssistantBubble
 * -----------------
 *
 * September 2026
 *
 * A floating chat assistant available on every page. Rendered once at the
 * top of App.js, outside <Routes>, so it never unmounts on navigation and
 * its conversation survives moving between pages. Uses the same
 * session-backed AI provider connection as the rest of the app (checked via
 * GET /api/ai/key/status) -- the provider and model are the ones connected on
 * the AI Settings page; the bubble has no connect form or model picker of its own.
 */
import * as React from "react";
import axios from "axios";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { AI_PROVIDER_CHANGED, describeProviderStatus, toProviderStatus } from "../Settings/useAIProviderStatus.js";
import "./AssistantBubble.css";

// The name shown on the chat panel and button. Change it here, or set
// REACT_APP_ASSISTANT_NAME when building the frontend.
export const ASSISTANT_NAME = process.env.REACT_APP_ASSISTANT_NAME || "AI Assistant";

const PANEL_SIZE_KEY = "assistant.panelSize";
const DEFAULT_SIZE = { width: 320, height: 440 };
const MIN_SIZE = { width: 280, height: 300 };

function loadPanelSize() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(PANEL_SIZE_KEY));
    if (saved?.width > 0 && saved?.height > 0) return saved;
  } catch {
    // Best-effort only -- falls back to the default size.
  }
  return DEFAULT_SIZE;
}

// Keeps the panel on screen: it's anchored to the bottom-right corner.
function clampSize({ width, height }) {
  return {
    width: Math.round(Math.min(Math.max(width, MIN_SIZE.width), window.innerWidth - 48)),
    height: Math.round(Math.min(Math.max(height, MIN_SIZE.height), window.innerHeight - 120)),
  };
}

/**
 * @param {Object} context - Free-form summary of what the user is currently
 * looking at (route + a compact snapshot of relevant state), sent with every
 * chat turn so replies can be grounded in the current page.
 */
function AssistantBubble({ context }) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  // Minimized: only the title bar shows; the conversation and draft are kept.
  const [minimized, setMinimized] = React.useState(false);
  const [unread, setUnread] = React.useState(false);
  const minimizedRef = React.useRef(false);
  minimizedRef.current = minimized;
  const [panelSize, setPanelSize] = React.useState(() => clampSize(loadPanelSize()));

  // Drag the top-left corner: the panel is anchored bottom-right, so moving
  // the pointer up/left makes it bigger.
  const startResize = (event) => {
    event.preventDefault();
    const start = { x: event.clientX, y: event.clientY, ...panelSize };
    let latest = panelSize;
    const onMove = (moveEvent) => {
      latest = clampSize({
        width: start.width + (start.x - moveEvent.clientX),
        height: start.height + (start.y - moveEvent.clientY),
      });
      setPanelSize(latest);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      try {
        window.localStorage.setItem(PANEL_SIZE_KEY, JSON.stringify(latest));
      } catch {
        // Best-effort only.
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  // null until checked; then the AI Settings connection (see useAIProviderStatus).
  const [provider, setProvider] = React.useState(null);
  const hasKey = provider ? provider.hasKey : null;
  const [messages, setMessages] = React.useState([]);
  const [draft, setDraft] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Picks up connects/disconnects made on the AI Settings page while the chat is up.
  React.useEffect(() => {
    const onChange = (event) => setProvider(event.detail);
    window.addEventListener(AI_PROVIDER_CHANGED, onChange);
    return () => window.removeEventListener(AI_PROVIDER_CHANGED, onChange);
  }, []);

  const toggleMinimized = () => {
    setMinimized(!minimized);
    setUnread(false);
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    setMinimized(false);
    setUnread(false);
    // Re-checked on every open: the user may have connected, disconnected
    // or switched providers on the AI Settings page since the last one.
    if (next) {
      axios
        .get("/api/ai/key/status")
        .then((res) => setProvider(toProviderStatus(res.data)))
        .catch(() => setProvider(toProviderStatus(null)));
    }
  };

  const sendMessage = () => {
    const text = draft.trim();
    if (!text || sending) return;

    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setDraft("");
    setSending(true);
    setError(null);

    axios
      .post("/api/assistant/chat", { context, messages: nextMessages })
      .then((res) => {
        setMessages((prev) => [...prev, { role: "assistant", content: res.data.reply }]);
        if (minimizedRef.current) setUnread(true);
      })
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to reach the assistant.");
        setMessages(messages);
      })
      .finally(() => setSending(false));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="assistant-bubble-root">
      {open && (
        <div
          className={"assistant-panel" + (minimized ? " assistant-panel-minimized" : "")}
          style={minimized ? { width: panelSize.width } : { width: panelSize.width, height: panelSize.height }}
        >
          {!minimized && (
            <div
              className="assistant-resize-handle"
              onPointerDown={startResize}
              title="Drag to resize"
              aria-label="Resize chat"
            />
          )}
          <div className="assistant-panel-header" onClick={minimized ? toggleMinimized : undefined}>
            <span>
              <i className="pi pi-sparkles" style={{ marginRight: "0.4rem" }} />
              {ASSISTANT_NAME}
              {minimized && (sending || unread) && (
                <span className="assistant-header-status">{sending ? "Thinking…" : "● New reply"}</span>
              )}
            </span>
            <span className="assistant-header-buttons">
              <Button
                icon={minimized ? "pi pi-window-maximize" : "pi pi-minus"}
                text
                rounded
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleMinimized();
                }}
                aria-label={minimized ? "Restore chat" : "Minimize chat"}
                title={minimized ? "Restore" : "Minimize"}
              />
              <Button
                icon="pi pi-times"
                text
                rounded
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleOpen();
                }}
                aria-label="Close chat"
                title="Close"
              />
            </span>
          </div>

          {!minimized && hasKey === false && (
            <div className="assistant-panel-body">
              <Message
                severity="info"
                text="Connect an AI provider on the AI Settings page to chat."
                style={{ width: "100%" }}
              />
              <Button
                label="Go to AI Settings"
                text
                onClick={() => navigate("/AISettings")}
                style={{ marginTop: "0.5rem" }}
              />
            </div>
          )}

          {!minimized && hasKey && (
            <>
              <div className="assistant-model-note">
                Using {describeProviderStatus(provider)} · <Link to="/AISettings">change</Link>
              </div>
              <div className="assistant-panel-body assistant-messages">
                {messages.length === 0 && (
                  <div className="assistant-empty-state">
                    Ask about setting up your model, your results, or anything else in DESMA.
                  </div>
                )}
                {messages.map((message, index) => (
                  <div key={index} className={`assistant-message assistant-message-${message.role}`}>
                    {message.role === "assistant" ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    ) : (
                      message.content
                    )}
                  </div>
                ))}
                {sending && <div className="assistant-message assistant-message-assistant">Thinking…</div>}
                {error && <Message severity="error" text={error} style={{ marginTop: "0.5rem" }} />}
              </div>
              <div className="assistant-panel-footer">
                <InputTextarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  autoResize
                  placeholder="Ask a question…"
                  style={{ flex: 1 }}
                />
                <Button icon="pi pi-send" onClick={sendMessage} disabled={!draft.trim() || sending} />
              </div>
            </>
          )}

          {!minimized && hasKey === null && <div className="assistant-panel-body">Checking connection…</div>}
        </div>
      )}
      <Button
        className="assistant-fab"
        icon={open ? "pi pi-times" : "pi pi-comments"}
        rounded
        onClick={toggleOpen}
        aria-label={ASSISTANT_NAME}
        tooltip={open ? undefined : ASSISTANT_NAME}
        tooltipOptions={{ position: "left" }}
      />
    </div>
  );
}

export default AssistantBubble;
